import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, IconButton, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { BsPlusCircle } from 'react-icons/bs';
import moment from 'moment';
import Utils from 'commons/utils';
import {
  Table,
  SearchInputColumnNew,
  SelectionSearchColumn,
  DatePicker,
  DatePickerSearchColumn,
  ModalFormDetail,
} from 'components';
import DeleteIcon from '@mui/icons-material/Delete';
import ApiServices from 'services';
import { RenderTaskJobStatus } from 'components/custom-status';
import { RESPONSE_STATUSES, TYPE_CLIENT_OPTIONS } from 'commons/constants';
import { debounce } from 'lodash';
import { ContainerHeader, ButtonAdd } from './styles';

import { FormDetail } from './form-detail';

const START_DATE = moment().startOf('month').format();
const END_DATE = moment().endOf('date').format();

const DEFAULT_FILTER = {
  pageNumber: 1,
  pageSize: 10,
  fromDate: START_DATE,
  toDate: END_DATE,
};

const Gifts = ({ clientCareHistoryItem, clientInformationDetail }) => {
  const [subJobList, setSubJobList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [searchStatus, setSearchStatus] = useState('all');
  const [searchBeginWork, setSearchBeginWork] = useState(null);
  const [searchEndWork, setSearchEndWork] = useState(null);

  const [selected, setSelected] = useState(null);

  const [searchClientType, setSearchClientType] = useState('2');

  const [totalRecords, setTotalRecords] = useState(0);

  const [startDate, setStartDate] = useState(START_DATE);
  const [endDate, setEndDate] = useState(END_DATE);

  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const handleClickOpenConfirm = (rowData) => {
    if (!rowData) return;
    setDeleteId(rowData);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setDeleteId(null);
    setOpenConfirm(false);
  };

  const initData = async (query, newClientCompanyId) => {
    const dataSend = {
      ...query,
      fromDate: query?.fromDate ? Utils.convertToDateFormat(query?.fromDate) : null,
      toDate: query?.toDate ? Utils.convertToDateFormat(query?.toDate) : null,
    };

    try {
      const res = await ApiServices.getPagingClientGiftByCCId(newClientCompanyId, dataSend);
      if (res?.data?.data) {
        if (totalRecords !== res?.data?.totalRecords) {
          setTotalRecords(res?.data?.totalRecords);
        }
        setSubJobList(res?.data?.data);
      }
    } catch (error) {
      console.log('get data init failed:::', error);
    }
  };

  const timeoutGetData = useCallback(
    debounce((newFilter, newClientCompanyId) => {
      initData(newFilter, newClientCompanyId);
    }, 500),
    [],
  );
  useEffect(() => {
    timeoutGetData(filter, clientInformationDetail?.id);
  }, [filter, clientInformationDetail?.id]);

  const reloadData = (query) => {
    initData(query, clientInformationDetail?.id);
  };

  const handleSearchColumn = (event) => {
    setFilter((prev) => {
      const searchArray = { ...prev.searchArray };
      const { value, name } = event.target;
      if (!value) {
        delete searchArray[name];
        return { ...prev, pageNumber: 1, searchArray };
      }
      searchArray[name] = value;
      return { ...prev, pageNumber: 1, searchArray };
    });
  };

  const handleSearchSelect = (event) => {
    let value = event.target.value;
    let name = event.target.name;

    setSearchClientType(value);

    if (value === '2' || value === 2) value = '';

    setFilter((prev) => {
      const searchArray = { ...prev.searchArray };
      if (!value && value !== 0) {
        delete searchArray[name];
        return { ...prev, pageNumber: 1, searchArray };
      }
      searchArray[name] = `${value}`;
      return { ...prev, pageNumber: 1, searchArray };
    });
  };
  const handleChangeSearchBeginWork = (event) => {
    const value = event.target.value;
    const name = event.target.name;
    setFilter((prev) => {
      const searchArray = { ...prev.searchArray };
      if (!value && value !== 0) {
        delete searchArray[name];
        return { ...prev, pageNumber: 1, searchArray };
      }
      searchArray[name] = value;
      return { ...prev, pageNumber: 1, searchArray };
    });
  };
  const handleChangeSearchEndWork = (event) => {
    const value = event.target.value;
    const name = event.target.name;
    setFilter((prev) => {
      const searchArray = { ...prev.searchArray };
      if (!value && value !== 0) {
        delete searchArray[name];
        return { ...prev, pageNumber: 1, searchArray };
      }
      searchArray[name] = value;
      return { ...prev, pageNumber: 1, searchArray };
    });
  };

  const handleSelectedCreate = () => {
    setSelected({ isNew: true });
  };

  const validateValue = (row) => {
    const dataSelect = row;
    dataSelect.giftSendDate = Utils.checkIsDate(row.giftSendDate)
      ? Utils.convertToDateFormat(row.giftSendDate)
      : '';
    return dataSelect;
  };
  const handleUpdate = (row) => {
    const dataSelect = validateValue(row);
    setSelected(dataSelect);
  };

  const deleteItem = async (row) => {
    if (!row?.id || loading) return;

    setLoading(true);
    return ApiServices.deleteClientGift(row?.id)
      .then((res) => {
        if (res.status === RESPONSE_STATUSES.OK) {
          window.UIMessage?.success('Xóa thành công');
          reloadData();
        }
      })
      .catch((err) => {
        window.UIMessage?.error('Xóa thất bại !!!');
        console.warn('deleteClientGift', err);
      })
      .finally(() => {
        setLoading(false);
        handleCloseConfirm();
      });
  };

  const renderCustomerType = (type) => {
    switch (type) {
      case 0:
        return 'Khách hàng tiềm năng';
      case 1:
        return 'Khách hàng hiện hữu';
      default:
        break;
    }
  };

  const columns = [
    {
      id: 'code',
      numeric: true,
      disablePadding: false,
      label: <SearchInputColumnNew title="Mã quà tặng" name="code" onChange={handleSearchColumn} />,
      align: 'center',
      width: '100px',
    },
    {
      id: 'status',
      numeric: true,
      disablePadding: false,
      label: (
        <>
          <b>Loại khách hàng</b>
          <SelectionSearchColumn
            size="small"
            label=""
            name="typeClientCompany"
            dataArrObject={TYPE_CLIENT_OPTIONS}
            value={searchClientType}
            onChange={handleSearchSelect}
            displayNone={false}
          />
        </>
      ),

      align: 'center',
      width: '140px',
      render: (row) => renderCustomerType(row?.typeClientCompany),
    },
    {
      id: 'clientCompanyName',
      numeric: true,
      disablePadding: false,
      label: (
        <SearchInputColumnNew
          title="Khách hàng"
          name="clientCompanyName"
          onChange={handleSearchColumn}
        />
      ),
      align: 'center',
      width: '160px',
    },
    {
      id: 'realReceiver',
      numeric: true,
      disablePadding: false,
      label: (
        <SearchInputColumnNew
          title="Người nhận"
          name="realReceiver"
          onChange={handleSearchColumn}
        />
      ),
      align: 'center',
      width: '160px',
    },
    {
      id: 'typeGiftName',
      numeric: true,
      disablePadding: false,
      label: (
        <SearchInputColumnNew
          title="Loại quà tặng"
          name="typeGiftName"
          onChange={handleSearchColumn}
        />
      ),
      align: 'center',
      width: '120px',
    },
    {
      id: 'priceGift',
      numeric: true,
      align: 'center',
      disablePadding: false,
      label: 'Giá trị',
      // label: <SearchInputColumn title="Giá trị" name="priceGift" onChange={handleSearchColumn} />,
      align: 'right',
      render: (row) => Utils.formatNumber(row?.priceGift),
    },
    {
      id: 'note',
      numeric: true,
      align: 'center',
      disablePadding: false,
      label: 'Ghi chú',
      align: 'left',
    },
    {
      id: 'giftSendDate',
      numeric: true,
      disablePadding: false,
      label: (
        <>
          <b>Ngày tặng</b>
          {/* <br />
          <DatePickerSearchColumn
            sx={{
              marginBottom: '10px',
              padding: 0,
            }}
            width={'140px'}
            marginTop="0"
            size="small"
            onChange={handleChangeSearchBeginWork}
            value={searchBeginWork}
            error={true}
            label=""
            name="giftSendDate"
          /> */}
        </>
      ),
      align: 'center',
      width: '140px',
      render: (row) => Utils.formatDateFn(row?.giftSendDate),
    },
    {
      id: 'createdOn',
      numeric: true,
      disablePadding: false,
      label: (
        <>
          <b>Ngày tạo</b>
          {/* <br />
          <DatePickerSearchColumn
            sx={{
              marginBottom: '10px',
              padding: 0,
            }}
            width={'140px'}
            marginTop="0"
            size="small"
            onChange={handleChangeSearchEndWork}
            value={searchEndWork}
            error={true}
            label=""
            name="createdOn"
          /> */}
        </>
      ),
      align: 'center',
      width: '80px',
      render: (row) => Utils.formatDateFn(row?.createdOn),
    },

    {
      id: 'id',
      numeric: true,
      disablePadding: false,
      label: '#',
      align: 'center',
      fixed: 'right',
      width: '60px',
      render: (row) => (
        <Stack direction="row">
          <IconButton color="primary" size="small" onClick={() => handleUpdate(row)}>
            <BorderColorOutlinedIcon />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            // onClick={() => deleteItem(row)}
            onClick={() => handleClickOpenConfirm(row)}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const handleChangeStartDate = useCallback(
    (event) => {
      setStartDate(event.target.value);
    },
    [startDate],
  );
  const handleChangeEndDate = useCallback(
    (event) => {
      setEndDate(event.target.value);
    },
    [endDate],
  );

  const handleSearchDate = (startDateSearch, endDateSearch) => {
    if (!startDateSearch || !endDateSearch) return;
    setFilter((prev) => ({ ...prev, fromDate: startDateSearch, toDate: endDateSearch }));
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 10px 10px 10px',
        }}
      >
        <ContainerHeader>
          <Stack spacing={2} direction="row">
            <Box sx={{ width: '160px' }}>
              <DatePicker
                sx={{
                  marginBottom: '10px',
                  padding: 0,
                }}
                width={100}
                marginTop="0"
                size="small"
                onChange={handleChangeStartDate}
                value={startDate}
                error={true}
                label="Từ ngày"
              />
            </Box>
            <Box sx={{ width: '160px' }}>
              <DatePicker
                sx={{
                  marginBottom: '10px',
                  padding: 0,
                }}
                width={100}
                marginTop="0"
                size="small"
                onChange={handleChangeEndDate}
                value={endDate}
                error={true}
                label="Đến ngày"
              />
            </Box>

            <IconButton
              aria-label="filter"
              sx={{ color: 'blue' }}
              onClick={() => handleSearchDate(startDate, endDate)}
            >
              <SearchIcon />
            </IconButton>
          </Stack>
        </ContainerHeader>
        <ButtonAdd variant="contained" startIcon={<BsPlusCircle />} onClick={handleSelectedCreate}>
          Thêm mới
        </ButtonAdd>
      </Box>
      <Table
        rows={subJobList || []}
        headCells={columns}
        totalRecords={totalRecords}
        onPageChange={(paging) => {
          setFilter((prev) => ({ ...prev, ...paging }));
        }}
        isHeader={false}
        // onClickDetail={handleClickDetail}
      />
      <FormDetail
        {...{
          selected,
          setSelected,
          clientInformationDetail,
          reloadData: () => reloadData(filter),
        }}
      />
      <ModalFormDetail
        maxWidth="xs"
        open={openConfirm}
        onCloseModal={handleCloseConfirm}
        title="Bạn có chắc chắn xóa?"
      >
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box
            style={{
              marginLeft: 'auto',
            }}
          >
            <Button onClick={handleCloseConfirm} color="error">
              Hủy
            </Button>
            <Button onClick={() => deleteItem(deleteId)} color="primary" autoFocus>
              Đồng Ý
            </Button>
          </Box>
        </Box>
      </ModalFormDetail>
    </>
  );
};

export { Gifts };
